import React, { memo, useCallback, useEffect, useMemo, useRef, useState, } from "react";
import { StyleSheet } from "react-native";
import invariant from "tiny-invariant";
import { useScreenSize } from "../../../hooks/useScreenSize";
import { Text } from "../../atoms/text/Text";
import { FADE_DURATION } from "../carousel/Carousel.web";
import { Carousel } from "../carousel/Carousel.web";
import { style as tabsStyle } from "./Tabs.style";
import "./Tabs.web.css";
const Tab = memo(({ children, index, active, disabled = false, style: customStyle, onLayout }) => {
    const style = useMemo(() => tabsStyle(), []);
    return (React.createElement("button", { ref: onLayout, "aria-selected": active, className: "tab-button", "data-tab-index": index, "data-testid": "tab", disabled: disabled, role: "tab", tabIndex: active ? 0 : -1, type: "button", style: {
            marginLeft: style.tab.marginHorizontal,
            marginRight: style.tab.marginHorizontal,
            paddingBottom: style.tab.paddingBottom,
            ...(customStyle?.tab ? StyleSheet.flatten(customStyle.tab) : undefined),
        } },
        React.createElement(Text, { level: 3, style: [style.tabText, active && style.activeTabText, customStyle?.tabText], variant: "action" }, children)));
});
Tab.displayName = "Tab";
const TabList = ({ labels = [], tabIndex = 0, style: customStyle, onSelect }) => {
    const style = useMemo(() => tabsStyle(), []);
    const screenSize = useScreenSize();
    const tabListStyle = (screenSize === "M" || screenSize === "L") && style.tabsLarge;
    const tabListRef = useRef(null);
    const tabRefs = useRef([]);
    const [tabItemPositions, setTabItemPositions] = useState({});
    const measureAll = useCallback(() => {
        if (!tabListRef.current)
            return;
        const listRect = tabListRef.current.getBoundingClientRect();
        setTabItemPositions((prev) => {
            const next = {};
            let changed = false;
            tabRefs.current.forEach((el, index) => {
                if (!el)
                    return;
                const tabRect = el.getBoundingClientRect();
                const left = Math.round(tabRect.left - listRect.left);
                const width = Math.round(tabRect.width);
                next[index] = { left, width };
                if (prev[index]?.left !== left || prev[index]?.width !== width)
                    changed = true;
            });
            return changed ? next : prev;
        });
    }, []);
    useEffect(() => {
        if (!tabListRef.current)
            return;
        const observer = new ResizeObserver(() => {
            requestAnimationFrame(measureAll);
        });
        observer.observe(tabListRef.current);
        return () => observer.disconnect();
    }, [measureAll]);
    const onLayoutHandlers = useMemo(() => labels.map((label, index) => (el) => {
        tabRefs.current[index] = el;
        requestAnimationFrame(measureAll);
    }), 
    // oxlint-disable-next-line react/exhaustive-deps
    [labels.length, measureAll]);
    const indicatorLeft = tabItemPositions[tabIndex]?.left ?? 0;
    const indicatorWidth = tabItemPositions[tabIndex]?.width ?? 0;
    const indicatorStyle = useMemo(() => ({
        ...style.indicator,
        position: "absolute",
        bottom: 0,
        left: 0,
        transform: `translateX(${indicatorLeft}px)`,
        width: indicatorWidth,
        transition: `transform ${FADE_DURATION}ms ease, width ${FADE_DURATION}ms ease`,
    }), [indicatorLeft, indicatorWidth, style.indicator]);
    const handleTabClick = useCallback((e) => {
        const target = e.target.closest("[data-tab-index]");
        if (!target)
            return;
        const idx = Number(target.dataset.tabIndex);
        if (!Number.isNaN(idx))
            onSelect?.(idx);
    }, [onSelect]);
    const handleKeyDown = useCallback((e) => {
        const count = labels.length;
        let next = tabIndex;
        if (e.key === "ArrowRight")
            next = (tabIndex + 1) % count;
        else if (e.key === "ArrowLeft")
            next = (tabIndex - 1 + count) % count;
        else if (e.key === "Home")
            next = 0;
        else if (e.key === "End")
            next = count - 1;
        else
            return;
        e.preventDefault();
        onSelect?.(next);
    }, [tabIndex, labels.length, onSelect]);
    return (React.createElement("div", { ref: tabListRef, className: "tab-list", "data-testid": "tab-list", role: "tablist", style: {
            display: "flex",
            alignItems: style.tabs.alignItems,
            backgroundColor: style.tabs.backgroundColor,
            flexDirection: style.tabs.flexDirection,
            justifyContent: (tabListStyle
                ? tabListStyle.justifyContent
                : style.tabs.justifyContent),
            paddingLeft: style.tabs.paddingHorizontal,
            paddingRight: style.tabs.paddingHorizontal,
            ...(customStyle?.tabList ? StyleSheet.flatten(customStyle.tabList) : undefined),
        }, onClick: handleTabClick, onKeyDown: handleKeyDown },
        labels.map((item, index) => (React.createElement(Tab, { key: index, active: tabIndex === index, index: index, style: customStyle?.tab, onLayout: onLayoutHandlers[index] }, item))),
        React.createElement("div", { "data-testid": "tab-indicator", style: indicatorStyle })));
};
const Tabs = ({ data, children, defaultIndex = 0, tabLabels, style: customStyle, onChanged, tabLabelsWrapper = (children) => children, animationType, }) => {
    invariant(data.length === tabLabels.length, "data and tab labels must be the same length");
    const style = useMemo(() => tabsStyle(), []);
    const [active, setActive] = useState(defaultIndex);
    const handleOnActiveChanged = useCallback((activeIndex) => setActive(activeIndex), []);
    useEffect(() => {
        setActive(defaultIndex);
    }, [defaultIndex]);
    useEffect(() => onChanged?.(active), [active, onChanged]);
    return (React.createElement("div", { "data-testid": "tabs", style: {
            ...style.container,
            ...(customStyle?.container ? StyleSheet.flatten(customStyle.container) : undefined),
            width: "100%",
        } },
        tabLabelsWrapper(React.createElement(TabList, { labels: tabLabels, style: customStyle?.tabList, tabIndex: active, onSelect: handleOnActiveChanged })),
        React.createElement("div", { style: customStyle?.carousel ? StyleSheet.flatten(customStyle.carousel) : undefined },
            React.createElement(Carousel, { key: animationType, activeIndex: active, animationType: animationType, data: data, enabled: false, onActiveIndexChanged: handleOnActiveChanged }, children))));
};
export { Tabs };
